/**
 * Production TextArea field — Tiptap on web.
 */
import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { parseTextAreaRequestId } from './TextArea.types';
import { TextAreaEditor } from './TextAreaEditor.web';
import { useInlineImageUpload } from './useInlineImageUpload.web';
import { useTextAreaEditorHtml } from './useTextAreaEditorHtml';
import { colors, fontSize, spacing } from '../../theme';
import { inlineImageFieldKey } from '../../utils/richContent/inlineAttachmentName';
import { reportTextAreaLength } from '../../utils/richContent/textAreaLength';
import { formFieldLabelText } from '../forms/formFieldLabelStyle';
import { formFieldInStack } from '../forms/formFieldStackContext';
import { RichHtmlContent } from '../RichHtmlContent';

import type { TextAreaProps } from './TextArea.types';

export function TextArea({
  label,
  value,
  onChange,
  maxLength,
  fieldLabel,
  requestId,
  isOnline,
  requestSection,
  error,
  disabled = false,
  hideLabel = false,
  placeholder,
  testID,
  containerStyle,
  transportFieldKey,
}: TextAreaProps): React.JSX.Element {
  const numericRequestId = parseTextAreaRequestId(requestId);
  const resolvedFieldLabel = fieldLabel ?? label;
  const resolvedInlineImageFieldKey = inlineImageFieldKey(
    requestSection,
    resolvedFieldLabel,
  );
  const { editorHtml, persistHtml } = useTextAreaEditorHtml(value, requestId);
  const resolveInlineImage = useInlineImageUpload({
    requestId: numericRequestId,
    requestSection,
    isOnline,
    fieldLabel: resolvedFieldLabel,
    fieldHtml: value,
    transportFieldKey,
  });
  // The ceiling is this field's Zod rule, so the count and the over-limit
  // message are rendered here rather than inside the editor control.
  const length = useMemo(
    () => reportTextAreaLength(value, maxLength, resolvedFieldLabel),
    [maxLength, resolvedFieldLabel, value],
  );

  return (
    <View
      style={[styles.container, formFieldInStack, containerStyle]}
      testID={testID}
    >
      {!hideLabel ? (
        <View style={styles.labelBand}>
          <Text
            style={styles.label}
            accessibilityRole="text"
            nativeID={`${label}-label`}
          >
            {label}
          </Text>
        </View>
      ) : null}
      {disabled ? (
        <RichHtmlContent
          value={value}
          requestId={requestId}
          testID={testID ? `${testID}-readonly` : undefined}
        />
      ) : (
        <>
          <TextAreaEditor
            initialHtml={editorHtml}
            onHtmlChange={html => onChange(persistHtml(html))}
            onResolveImage={resolveInlineImage}
            placeholder={placeholder}
            inlineImageFieldKey={resolvedInlineImageFieldKey}
          />
          <View
            style={styles.charCountRow}
            testID={testID ? `${testID}-char-count` : undefined}
          >
            <Text
              style={styles.charCount}
              accessibilityLabel={`${length.plainTextLength} of ${maxLength} characters`}
            >
              {length.plainTextLength} / {maxLength}
            </Text>
          </View>
          {length.zodMessage ? (
            <Text
              style={styles.error}
              accessibilityRole="alert"
              testID={testID ? `${testID}-length-error` : undefined}
            >
              {length.zodMessage}
            </Text>
          ) : null}
        </>
      )}
      {error ? (
        <Text
          style={styles.error}
          accessibilityRole="alert"
          accessibilityLabel={error}
          nativeID={`${label}-error`}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minWidth: 0,
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
  /**
   * Gap on a View, not Text — RN-web often ignores bottom margin on text nodes
   * (`FormFieldLabelWithInfo` `labelBand` / `FormInput` `labelContainer`).
   */
  labelBand: {
    width: '100%' as const,
    marginBottom: spacing.fieldVerticalGap,
  },
  label: {
    ...formFieldLabelText,
  },
  error: {
    fontSize: fontSize.charCount,
    color: colors.error,
    marginTop: spacing.xs,
  },
  charCountRow: {
    alignItems: 'flex-end',
    marginTop: spacing.charCountMarginTop,
  },
  charCount: {
    fontSize: fontSize.charCount,
    color: colors.textSecondary,
  },
});
